import type { ExcelDownloadClient } from "app/components/React/ExcelButton/ExcelClientDownload";
import type { IScoringConfigExportDto } from "app/pages/sales-intelligence/domain/leads/types/scoring-config";
import type { SignalEventDto } from "app/pages/sales-intelligence/sub-modules/account-overview/company/types/signals";
import type {
    CommonRequestOptions,
    GetRequestOptions,
    IFetchService,
    PostConfigObject,
    PutConfigObject,
} from "app/services/fetchService";

import type { IPermanentSignal } from "../../models/core/Signal";
import type Signal from "../../models/core/Signal";
import type SignalFilters from "../../models/core/SignalFilters";
import type SignalId from "../../models/core/SignalId";
import type SignalCreateDTO from "../../models/miscellaneous/SignalCreateDTO";
import type { SignalsDescriptionResponseDto } from "../../models/miscellaneous/SignalsDescription";
import type SignalUpdateDTO from "../../models/miscellaneous/SignalUpdateDTO";
import type { IScoringConfigDto } from "../../types";
import type { ScoringConfigRule, ScoringConfigType } from "../../types/scoring-config";

const SIGNALS_API_PREFIX = "/sales-api/signals";
const ACCOUNT_SETTINGS_URL = `${SIGNALS_API_PREFIX}/account/settings`;

export type AccountSignalSettings = {
    allowUserCustomization: boolean;
};

export default function createSignalsApiService(
    fetchService: IFetchService,
    excelDownloadClient: ExcelDownloadClient,
) {
    return {
        fetchOpenAISignalsSummary(
            domain: string,
            signalEvents: SignalEventDto[],
            otherSignalEvents: SignalEventDto[],
            options?: GetRequestOptions,
        ): Promise<{ data: string }> {
            return fetchService.post(
                `/gw/openai/completion/website/signals`,
                { domain, signalEvents, otherSignalEvents },
                options,
            );
        },
        fetchSignals(options?: GetRequestOptions): Promise<{ signals: Signal[]; totalCount: number }> {
            return fetchService.get(SIGNALS_API_PREFIX, undefined, options);
        },
        fetchSignalsDescription(options?: GetRequestOptions): Promise<SignalsDescriptionResponseDto> {
            return fetchService.get(`${SIGNALS_API_PREFIX}/description`, undefined, options);
        },
        createSignals<F extends SignalFilters>(
            dtos: readonly SignalCreateDTO<F>[],
            options?: PostConfigObject,
        ): Promise<Signal<F>[]> {
            return fetchService.post(SIGNALS_API_PREFIX, dtos, options);
        },
        updateSignal<F extends SignalFilters>(
            dto: Readonly<SignalUpdateDTO<F>>,
            options?: PutConfigObject,
        ): Promise<Signal<F>> {
            return fetchService.put(`${SIGNALS_API_PREFIX}/${dto.id}`, dto, options);
        },
        deleteSignal(signalId: Signal["id"], options?: CommonRequestOptions): Promise<void> {
            return fetchService.delete(`${SIGNALS_API_PREFIX}/${signalId}`, undefined, undefined, options);
        },

        transitionSignalToPermanent<F extends SignalFilters = SignalFilters>(
            signalId: SignalId,
            options?: PostConfigObject,
        ): Promise<IPermanentSignal<F>> {
            return fetchService.put(`${SIGNALS_API_PREFIX}/make-permanent/${signalId}`, undefined, options);
        },

        fetchScoringConfig(configType: ScoringConfigType, options?: GetRequestOptions): Promise<IScoringConfigDto> {
            return fetchService.get(`${SIGNALS_API_PREFIX}/scoring-config/${configType}`, undefined, options);
        },

        updateScoringConfig(
            configType: ScoringConfigType,
            rules: ScoringConfigRule,
            options?: PutConfigObject,
        ): Promise<void> {
            return fetchService.put(`${SIGNALS_API_PREFIX}/scoring-config/${configType}`, rules, options);
        },

        /** Streams back an xlsx built from the already formatted rows; the browser downloads it directly. */
        exportScoringConfigToExcel(dto: IScoringConfigExportDto) {
            return excelDownloadClient(`${SIGNALS_API_PREFIX}/scoring-config/export/excel`, dto);
        },

        getAccountSettings(options?: GetRequestOptions): Promise<AccountSignalSettings> {
            return fetchService.get(ACCOUNT_SETTINGS_URL, undefined, options);
        },
        updateAccountSettings(
            settings: AccountSignalSettings,
            options?: PutConfigObject,
        ): Promise<AccountSignalSettings> {
            return fetchService.put(ACCOUNT_SETTINGS_URL, settings, options);
        },
    };
}
